// Lookahead scheduler ("A Tale of Two Clocks"): a coarse setInterval timer wakes
// up periodically and asks the consumer to schedule any events falling inside a
// short lookahead window on the *audio* clock. This keeps event timing
// sample-accurate regardless of UI frame rate or timer jitter.
//
// `now` is injected (ctx.currentTime in production, a fake in tests) so the
// timing logic that drives it stays unit-testable.

export interface SchedulerOptions {
  /** How far ahead to schedule, in seconds. */
  lookahead?: number;
  /** Timer wake-up interval, in milliseconds. */
  intervalMs?: number;
}

export class LookaheadScheduler {
  private readonly now: () => number;
  private readonly lookahead: number;
  private readonly intervalMs: number;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(now: () => number, opts: SchedulerOptions = {}) {
    this.now = now;
    this.lookahead = opts.lookahead ?? 0.1;
    this.intervalMs = opts.intervalMs ?? 25;
  }

  get running(): boolean {
    return this.timer !== null;
  }

  start(tick: (horizon: number) => void): void {
    if (this.timer !== null) return;
    const run = (): void => tick(this.now() + this.lookahead);
    this.timer = setInterval(run, this.intervalMs);
    run(); // schedule the first window immediately
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
